import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphRenderState, HdBodygraphSide, HdPlanetGridVisualizationMode } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { getHdPlanetDisplayConfig, HdPlanetDisplayConfig } from '../hd-planet-display-config';
import { findActivationFromList, isGateInDefinedCenter } from '../hd-planet-grid.utils';
import { HdPlanetActivationBlockClickEvent, HdPlanetActivationBlockComponent, HdPlanetGridDensity } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import { HdColorToneBlockComponent, HdPhsBlockClickEvent } from '../hd-color-tone-block/hd-color-tone-block.component';
import { HdTransformationArrowComponent } from '../hd-transformation-arrow/hd-transformation-arrow.component';
import { PHS_CATEGORIES, PhsCategory, phsCategoryFor } from '../../hd-phs-metadata';
import { HdPhsInfoService } from '../../hd-phs-info.service';

interface HdIncarnationCrossBlockViewModel {
  activation: HdBodygraphPlanetaryActivation;
  displayConfig: HdPlanetDisplayConfig;
  isDefined: boolean;
  geneKeysLabel: string | null;
}

/**
 * Groups Sun + Earth blocks with their Motivation (Color/Tone) indicator.
 * For personality: [Earth] [Sun] [Motivation]
 * For design (mirrored): [Motivation] [Sun] [Earth]
 */
@Component({
  selector: 'hd-incarnation-cross',
  templateUrl: './hd-incarnation-cross.component.html',
  styleUrl: './hd-incarnation-cross.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdPlanetActivationBlockComponent, HdColorToneBlockComponent, HdTransformationArrowComponent],
  host: {
    '[class.is-furnishings-dimmed]': 'furnishingsDimmed()',
  },
})
export class HdIncarnationCrossComponent {
  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly side = input.required<HdBodygraphSide>();
  readonly renderState = input.required<HdBodygraphRenderState>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  readonly showGeneKeys = input(false);
  readonly showHdKeynotes = input(false);
  /** Classic (enkel cijfers) vs extended (PHS-iconen) weergave. */
  readonly visualizationMode = input<HdPlanetGridVisualizationMode>('extended');
  /** Mini-densiteit (mobiel): compacte twee-regel-blokken — zie {@link HdPlanetGridDensity}. */
  readonly density = input<HdPlanetGridDensity>('normal');
  /**
   * Per-planet (keyed by `planetId`) LPS gate that should highlight when
   * hovering the base badge of that planet's activation block. Resolved
   * upstream in `HdPlanetGridComponent` so cross-side lookups (link-node
   * crossover) stay in one place. See `.\docs\human-design\base-logic.md`.
   */
  readonly baseHoverTargetGates = input<ReadonlyMap<number, number>>(new Map());
  readonly blockClicked = output<HdPlanetActivationBlockClickEvent>();
  /**
   * Klik op het motivatie/determinatie color/tone-blok. Wordt door deze
   * cross verrijkt met de PHS-categorie + kleur/toon en doorgegeven aan de
   * planet-grid voor smart-placement van het info-panel.
   */
  readonly phsBlockClicked = output<HdPhsBlockClickEvent>();

  private readonly phsInfoService = inject(HdPhsInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });

  /**
   * Of de motivatie kleur/toon-blok + transformatiepijl moeten dimmen.
   * Tijdens een hover die niet de zon of aarde van deze zijde betreft worden
   * de bijhorende "furnishings" (de motivatie color/tone-blok en de pijl)
   * mee gedimd. Wanneer de hover wél matcht met sun of earth (zelfde gate of
   * een center waar sun/earth in zit) blijven ze helder — die info hoort
   * inhoudelijk bij de gehighlighte poort.
   */
  protected readonly furnishingsDimmed = computed(() => {
    const service = this.hoverService;
    if (!service || !service.enabled() || !service.isActive()) return false;
    if (!service.dimsGrid()) return false;
    const sun = this.sunBlock();
    const earth = this.earthBlock();
    const sunMatch = sun != null && service.isGateMatchingHover(sun.activation.gate);
    const earthMatch = earth != null && service.isGateMatchingHover(earth.activation.gate);
    return !(sunMatch || earthMatch);
  });

  protected readonly sunConfig = getHdPlanetDisplayConfig('sun');
  protected readonly earthConfig = getHdPlanetDisplayConfig('earth');

  protected readonly sunBlock = computed<HdIncarnationCrossBlockViewModel | null>(() =>
    this.createBlockViewModel(this.sunConfig),
  );

  protected readonly earthBlock = computed<HdIncarnationCrossBlockViewModel | null>(() =>
    this.createBlockViewModel(this.earthConfig),
  );

  protected readonly motivationColor = computed<number | null>(() =>
    this.sunBlock()?.activation.color ?? null,
  );

  protected readonly motivationTone = computed<number | null>(() =>
    this.sunBlock()?.activation.tone ?? null,
  );

  /** PHS-categorie van deze incarnation-cross: design → determinatie, personality → motivatie. */
  protected readonly phsCategory = computed<PhsCategory | null>(() => {
    const side = this.side();
    return side === 'design' || side === 'personality' ? phsCategoryFor(side, 'incarnation') : null;
  });

  private readonly phsCategoryInfo = computed(() => {
    const category = this.phsCategory();
    if (!category) return null;
    return this.phsInfoService.categoryInfoIn(category, this.contentLang.effectiveLang());
  });

  /** Verrijkt de child-klik met categorie/kleur/toon en bubblet naar de planet-grid. */
  protected onPhsBlockClicked(event: { origin: { x: number; y: number } }): void {
    const category = this.phsCategory();
    const color = this.motivationColor();
    const tone = this.motivationTone();
    const side = this.side();
    if (!category || color == null || tone == null) return;
    this.phsBlockClicked.emit({
      category,
      side,
      color,
      tone,
      colorConfig: this.colorConfig(),
      origin: event.origin,
    });
  }

  /** Kleur-icoon (kale FA-naam) voor het kleur/toon-blok in extended-modus. */
  protected readonly phsColorIcon = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const category = this.phsCategory();
    const color = this.motivationColor();
    if (!category || color == null) return null;
    return PHS_CATEGORIES[category].colors[color - 1]?.icon ?? null;
  });

  /** Cognitie-icoon (kale FA-naam) op basis van de toon, in extended-modus. */
  protected readonly phsCognitionIcon = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const category = this.phsCategory();
    const tone = this.motivationTone();
    if (!category || tone == null || tone < 1 || tone > 6) return null;
    return PHS_CATEGORIES[category].cognitionIcons[tone - 1] ?? null;
  });

  /** Hover-tooltip (keynote) voor het kleur-icoon, in extended-modus. */
  protected readonly phsColorTooltip = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const color = this.motivationColor();
    if (color == null || color < 1 || color > 6) return null;
    const info = this.phsCategoryInfo()?.colors[color - 1];
    if (!info) return null;
    return info.short ? `${info.name} — ${info.short}` : info.name;
  });

  /** Hover-tooltip (keynote) voor het cognitie-icoon, in extended-modus. */
  protected readonly phsCognitionTooltip = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const tone = this.motivationTone();
    if (tone == null || tone < 1 || tone > 6) return null;
    const cognition = this.phsCategoryInfo()?.cognition[tone - 1];
    if (!cognition) return null;
    return cognition.short ? `${cognition.name} — ${cognition.short}` : cognition.name;
  });

  /** Icoon van de actieve toon-variant (links 1-3 / rechts 4-6) van de kleur. */
  protected readonly phsVariantIcon = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const category = this.phsCategory();
    const color = this.motivationColor();
    const tone = this.motivationTone();
    if (!category || color == null || color < 1 || color > 6 || tone == null || tone < 1 || tone > 6) return null;
    const colorMeta = PHS_CATEGORIES[category].colors[color - 1];
    if (!colorMeta) return null;
    return tone <= 3 ? colorMeta.toneLeftIcon : colorMeta.toneRightIcon;
  });

  /** Hover-tooltip (keynote) voor het toon-variant-icoon, in extended-modus. */
  protected readonly phsVariantTooltip = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const color = this.motivationColor();
    const tone = this.motivationTone();
    if (color == null || color < 1 || color > 6 || tone == null || tone < 1 || tone > 6) return null;
    const info = this.phsCategoryInfo()?.colors[color - 1];
    if (!info) return null;
    const variant = tone <= 3 ? info.tone.left : info.tone.right;
    return variant.short ? `${variant.name} — ${variant.short}` : variant.name;
  });

  /** Naam van de actieve toon-variant (kalm/nerveus, …) — voor de keynotes-modus. */
  protected readonly phsVariantName = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const color = this.motivationColor();
    const tone = this.motivationTone();
    if (color == null || color < 1 || color > 6 || tone == null || tone < 1 || tone > 6) return null;
    const info = this.phsCategoryInfo()?.colors[color - 1];
    if (!info) return null;
    return tone <= 3 ? info.tone.left.name : info.tone.right.name;
  });

  /** Cognitie-naam o.b.v. de toon — voor de keynotes-modus. */
  protected readonly phsCognitionName = computed<string | null>(() => {
    if (this.visualizationMode() === 'classic') return null;
    const tone = this.motivationTone();
    if (tone == null || tone < 1 || tone > 6) return null;
    return this.phsCategoryInfo()?.cognition[tone - 1]?.name ?? null;
  });

  protected readonly isMirrored = computed(() => this.side() === 'design');

  /**
   * De transformatiepijl hangt absoluut náást het planet-grid-paneel; in
   * mini-densiteit (twee grids naast elkaar op een telefoon) is daar geen
   * ruimte voor, dus vervalt ze daar. De richting-informatie blijft afleidbaar
   * uit het toon-cijfer in het kleur/toon-blok.
   */
  protected readonly showTransformationArrow = computed(() =>
    this.density() === 'normal' && this.motivationTone() !== null,
  );

  /**
   * Poorten waar de motivatie kleur/toon-blok + transformatiepijl bij horen
   * (Sun + Earth van deze zijde). Wordt aan de furnishing-componenten
   * doorgegeven zodat hover op die elementen dezelfde chart-highlight
   * triggert als een hover op deze poorten zelf.
   */
  protected readonly sunBaseHoverGate = computed<number | null>(() => {
    const block = this.sunBlock();
    if (!block) return null;
    return this.baseHoverTargetGates().get(block.activation.planetId) ?? null;
  });

  protected readonly earthBaseHoverGate = computed<number | null>(() => {
    const block = this.earthBlock();
    if (!block) return null;
    return this.baseHoverTargetGates().get(block.activation.planetId) ?? null;
  });

  protected readonly furnishingGates = computed<readonly number[]>(() => {
    const gates: number[] = [];
    const sun = this.sunBlock();
    if (sun) gates.push(sun.activation.gate);
    const earth = this.earthBlock();
    if (earth) gates.push(earth.activation.gate);
    return gates;
  });

  private createBlockViewModel(config: HdPlanetDisplayConfig): HdIncarnationCrossBlockViewModel | null {
    const activation = findActivationFromList(this.activations(), this.side(), config.planetId);

    if (!activation) {
      return null;
    }

    const geneKeysLabel = this.showGeneKeys()
      ? config.geneKeysLabels[this.side()] ?? null
      : null;

    return {
      activation,
      displayConfig: config,
      isDefined: isGateInDefinedCenter(activation.gate, this.renderState()),
      geneKeysLabel,
    };
  }
}
